import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api';
import { Card, Alert, Table, Button } from 'flowbite-react';
import QuickStats from '../components/QuickStats';

export default function AppStats() {
  const { appUuid } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/app-dashboard?app_uuid=${encodeURIComponent(appUuid)}`);
        setData(res);
      } catch (e) {
        setError(e.message);
      }
    })();
  }, [appUuid]);

  if (error) return <Alert color="failure">{error}</Alert>;
  if (!data) return <Card><div>Загрузка...</div></Card>;

  const hasBotToken = !!data.app?.bot_token;
  const botUsername = data.app?.bot_username;
  const botLink = botUsername ? `https://t.me/${botUsername}` : null;
  const webappUrl = data.app?.webapp_url;

  return (
    <div className="grid gap-4">
      <QuickStats stats={data} />
      
      <Card>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h2 className="text-lg font-semibold">Приложение — {data.app?.name}</h2>
          <div className="flex flex-wrap gap-2">
            <Button as={Link} to={`/${appUuid}/users`} size="sm">Пользователи</Button>
            <Button as={Link} to={`/${appUuid}/properties`} size="sm">Объекты</Button>
            <Button as={Link} to={`/${appUuid}/broadcasts`} size="sm">Рассылки</Button>
          </div>
        </div>
      </Card>
      
      <Card>
        <h3 className="text-base font-semibold mb-3">Настройки приложения</h3>
        <div className="grid gap-3">
          <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-lg ${hasBotToken ? 'bg-blue-50' : 'bg-red-50'}`}>
            <div>
              <div className={`font-medium ${hasBotToken ? 'text-blue-900' : 'text-red-900'}`}>Telegram бот</div>
              <div className={`text-sm ${hasBotToken ? 'text-blue-700' : 'text-red-700'}`}>
                {hasBotToken ? (
                  <>
                    Бот настроен {data.app?.webhook_uuid ? '(webhook активен)' : '(webhook не настроен)'} — <Link className="underline underline-offset-2 decoration-blue-500" to={`/${appUuid}/bot`}>перейти к настройкам</Link>
                  </>
                ) : (
                  <>
                    Бот не подключен — <Link className="underline underline-offset-2 decoration-blue-500" to={`/${appUuid}/bot`}>подключить чат-бота</Link>
                  </>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {botLink && (
                <Button 
                  color="light" 
                  size="sm" 
                  onClick={() => window.open(botLink, '_blank')}
                >
                  Открыть бота
                </Button>
              )}
              <Button as={Link} to={`/${appUuid}/bot`} color="blue" size="sm">Настроить бота</Button>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 bg-green-50 rounded-lg">
            <div className="min-w-0">
              <div className="font-medium text-green-900">Веб-приложение</div>
              <div className="text-sm text-green-700 font-mono text-xs break-all">
                {webappUrl || 'URL не задан'}
              </div>
            </div>
            {webappUrl && (
              <Button 
                color="green" 
                size="sm" 
                onClick={() => window.open(webappUrl, '_blank')}
              >
                Открыть
              </Button>
            )}
          </div>
        </div>
      </Card>
      <Card>
        <h3 className="text-base font-semibold">Топ объектов</h3>
        {/* Mobile cards */}
        <div className="grid gap-3 sm:hidden">
          {data.chart.labels.length === 0 ? (
            <div className="text-sm text-gray-500">Нет данных</div>
          ) : (
            data.chart.labels.map((label, idx) => (
              <Card key={idx}>
                <div className="flex items-center justify-between">
                  <div className="text-sm break-words max-w-[70%]">{label}</div>
                  <div className="text-sm font-semibold">{data.chart.values[idx]}</div>
                </div>
              </Card>
            ))
          )}
        </div>
        {/* Desktop table */}
        <div className="overflow-x-auto hidden sm:block">
          <Table hoverable>
            <Table.Head>
              <Table.HeadCell>Объект</Table.HeadCell>
              <Table.HeadCell className="hidden sm:table-cell">Просмотры</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {data.chart.labels.map((label, idx) => (
                <Table.Row key={idx} className="bg-white dark:border-gray-700 dark:bg-gray-800">
                  <Table.Cell className="max-w-[240px] sm:max-w-none break-words">{label}</Table.Cell>
                  <Table.Cell className="hidden sm:table-cell font-semibold">{data.chart.values[idx]}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      </Card>
    </div>
  );
}